import { useEffect, useState } from "react";
import { listJobs, listSongs } from "../../infra/api.js";

/**
 * Polls /jobs for active generation jobs and cross-references with /songs
 * to determine which bands have a music being generated right now.
 *
 * Returns a Set of bandIds currently "playing".
 */
export function usePlayingBands(pollMs: number = 3000): Set<string> {
  const [playing, setPlaying] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function poll(): Promise<void> {
      try {
        const [pending, claimed, songsRes] = await Promise.all([
          listJobs({ status: "pending", kind: "generation" }),
          listJobs({ status: "claimed", kind: "generation" }),
          listSongs({}),
        ]);
        if (cancelled) return;
        const active = [...pending.jobs, ...claimed.jobs];
        const songMap = new Map<string, string | null>();
        for (const s of songsRes.songs) songMap.set(s.project_id, s.band_id);
        const bandIds = new Set<string>();
        for (const job of active) {
          const bandId = songMap.get(job.projectId);
          if (bandId) bandIds.add(bandId);
        }
        setPlaying(bandIds);
      } catch {
        // silent — ignore transient failures
      }
      if (!cancelled) {
        timer = setTimeout(() => void poll(), pollMs);
      }
    }
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [pollMs]);

  return playing;
}
